import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Template } from '../types/Template';

interface FloatingTemplateMenuProps {
  onSaveTemplate: () => void;
  onLoadTemplate: (templateData: any) => void;
  currentFormData: any;
}

const FloatingTemplateMenu: React.FC<FloatingTemplateMenuProps> = ({
  onSaveTemplate,
  onLoadTemplate,
  currentFormData
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setShowTemplateSelector(false);
    }
  };

  const handleSaveTemplate = () => {
    onSaveTemplate();
    setIsOpen(false);
  };

  const handleUseTemplate = async () => {
    setLoading(true);
    try {
      // Fetch templates from API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/templates?limit=50`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        const error = await response.json();
        console.error('Failed to fetch templates:', error.message);
        setTemplates([]);
      }
      setShowTemplateSelector(true);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
      setShowTemplateSelector(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template: Template) => {
    onLoadTemplate(template.formData);
    setShowTemplateSelector(false);
    setIsOpen(false);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Floating Menu Button */}
      <div className={`floating-menu ${isOpen ? 'open' : ''}`}>
        <button 
          className="floating-menu-trigger"
          onClick={toggleMenu}
          title="Template Menu"
        >
          {isOpen ? '×' : 'Template'}
        </button>
        
        {isOpen && (
          <div className="floating-menu-content">
            <button 
              className="menu-item"
              onClick={handleUseTemplate}
              disabled={loading}
            >
              {loading ? 'Laster...' : 'Bruk template'}
            </button>
            <button 
              className="menu-item"
              onClick={handleSaveTemplate}
            >
              Lagre template
            </button>
          </div>
        )}
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div className="template-selector-overlay" onClick={() => setShowTemplateSelector(false)}>
          <div className="template-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-selector-header">
              <h3>Velg Template</h3>
              <button 
                className="close-button"
                onClick={() => setShowTemplateSelector(false)}
              >
                ×
              </button>
            </div>
            
            <div className="template-selector-search">
              <input
                type="text"
                placeholder="Søk i templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="template-selector-list">
              {filteredTemplates.length === 0 ? (
                <div className="no-templates">
                  <p>Ingen templates funnet</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowTemplateSelector(false);
                      setIsOpen(false);
                      navigate('/templates');
                    }}
                  >
                    Gå til Templates
                  </button>
                </div>
              ) : (
                filteredTemplates.map(template => (
                  <div 
                    key={template._id} 
                    className="template-item"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="template-item-info">
                      <h4>{template.name}</h4>
                      <p>Opprettet av: {template.createdBy.firstName} {template.createdBy.lastName}</p>
                      <p>Dato: {new Date(template.createdAt).toLocaleDateString('no-NO')}</p>
                      <p>Avdeling: {template.department} • Brukt: {template.usageCount} ganger</p>
                    </div>
                    <div className="template-item-action">
                      <span>Bruk →</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingTemplateMenu;